import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import {
  Box, Button, Card, CardContent, CircularProgress, Stack, Typography,
  FormControl, InputLabel, Select, MenuItem, IconButton, Chip, Divider,
  Tooltip, TextField
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { toast } from 'react-toastify'
import { settingsApi, type KnowlarityNumber, type AddKnowlarityNumberPayload } from '../lib/api/settingsApi'
import { orgsApi, lobsApi } from '../lib/api/tenantsApi'
import { PageHeader } from '../components/shared/PageHeader'
import { themeColors } from '../app/layout/theme'
import { usePermission } from '../lib/hooks/usePermission'
import { authStore } from '../lib/stores/authStore'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'

// ── Empty form state ─────────────────────────────────────────────────────────

const emptyForm = { kNumber: '', callerId: '', label: '' }

export const KnowlaritySettingsPage = observer(function KnowlaritySettingsPage() {
  const queryClient = useQueryClient()
  const { hasAction, isSuperAdmin } = usePermission()
  const roleName = authStore.user?.roleName?.toUpperCase() ?? ''
  const canUpdate = isSuperAdmin || roleName.includes('ADMIN') || hasAction('/settings/knowlarity', 'manage')
  const canSetRetention = isSuperAdmin ||hasAction('/settings/knowlarity', 'setRetentionKnowlarityId')

  // ── User scope & locking flags ─────────────────────────────────────────────
  const userOrgId = authStore.user?.orgId && authStore.user.orgId !== '00000000-0000-0000-0000-000000000000' ? authStore.user.orgId : ''
  const userLobId = authStore.user?.lobId && authStore.user.lobId !== '00000000-0000-0000-0000-000000000000' ? authStore.user.lobId : ''

  const isOrgFixed = !isSuperAdmin && !!userOrgId
  const isLobFixed = !isSuperAdmin && !!userLobId

  // ── Scope selectors ───────────────────────────────────────────────────────
  const [selectedOrgId, setSelectedOrgId] = useState<string>('')
  const [selectedLobId, setSelectedLobId] = useState<string>('')

  // ── Retention customer calling scope state ─────────────────────────────
  const [retentionOrgId, setRetentionOrgId] = useState<string>('')
  const [retentionLobId, setRetentionLobId] = useState<string>('')

  // ── Add-form state ────────────────────────────────────────────────────────
  const [form, setForm] = useState(emptyForm)
  const [deleteTarget, setDeleteTarget] = useState<KnowlarityNumber | null>(null)

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: orgs = [], isLoading: isLoadingOrgs } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: orgsApi.getAll,
  })

  const { data: lobs = [], isLoading: isLoadingLobs } = useQuery({
    queryKey: ['admin-lobs', selectedOrgId],
    queryFn: () => lobsApi.getAll(selectedOrgId),
    enabled: !!selectedOrgId,
  })

  const { data: numbers = [], isLoading: isLoadingNumbers } = useQuery({
    queryKey: ['knowlarity-numbers', selectedOrgId, selectedLobId],
    queryFn: () => settingsApi.getKnowlarityNumbers(selectedOrgId, selectedLobId),
    enabled: !!selectedOrgId && !!selectedLobId,
  })

  // Retention setting query
  const { data: retentionSetting, isLoading: isLoadingRetention } = useQuery({
    queryKey: ['knowlarity-retention-setting'],
    queryFn: settingsApi.getRetentionKnowlaritySetting,
  })

  // Rotation query
  const { data: rotationConfig, isLoading: isLoadingRotation } = useQuery({
    queryKey: ['knowlarity-rotation', selectedOrgId, selectedLobId],
    queryFn: () => settingsApi.getKnowlarityRotation(selectedOrgId, selectedLobId),
    enabled: !!selectedOrgId && !!selectedLobId,
  })
  
  const [frequencyHours, setFrequencyHours] = useState<number | ''>('')
  
  useEffect(() => {
    if (rotationConfig) {
      setFrequencyHours(rotationConfig.frequencyHours)
    }
  }, [rotationConfig])

  const [timeLeft, setTimeLeft] = useState<string>('')

  useEffect(() => {
    if (!rotationConfig?.nextRotationAt) {
      setTimeLeft('')
      return
    }

    const nextDate = new Date(rotationConfig.nextRotationAt + (rotationConfig.nextRotationAt.endsWith('Z') ? '' : 'Z')).getTime()

    const updateTimer = () => {
      const now = new Date().getTime()
      const diff = nextDate - now
      if (diff <= 0) {
        setTimeLeft('Rotating soon...')
        return
      }

      const h = Math.floor(diff / (1000 * 60 * 60))
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const s = Math.floor((diff % (1000 * 60)) / 1000)

      setTimeLeft(`in ${h}h ${m}m ${s}s`)
    }

    updateTimer()
    const intervalId = setInterval(updateTimer, 1000)

    return () => clearInterval(intervalId)
  }, [rotationConfig?.nextRotationAt])

  const initialRetentionOrg = retentionSetting?.orgId ?? ''
  const initialRetentionLob = retentionSetting?.lobId ?? ''
  const isRetentionDirty = (retentionOrgId !== initialRetentionOrg) || (retentionLobId !== initialRetentionLob)

  useEffect(() => {
    if (retentionSetting) {
      if (retentionSetting.orgId && !isOrgFixed) setRetentionOrgId(retentionSetting.orgId)
      if (retentionSetting.lobId && !isLobFixed) setRetentionLobId(retentionSetting.lobId)
    }
  }, [retentionSetting, isOrgFixed, isLobFixed])

  const { data: retentionLobs = [], isLoading: isLoadingRetentionLobs } = useQuery({
    queryKey: ['admin-retention-lobs', retentionOrgId],
    queryFn: () => lobsApi.getAll(retentionOrgId),
    enabled: !!retentionOrgId,
  })

  // ── Auto-select & Lock Org / LOB from user profile context or single options ─────
  useEffect(() => {
    if (isOrgFixed && userOrgId) {
      setSelectedOrgId(userOrgId)
      if (!retentionOrgId) setRetentionOrgId(userOrgId)
    } else if (!selectedOrgId && orgs.length > 0) {
      if (userOrgId && orgs.some(o => o.id === userOrgId)) {
        setSelectedOrgId(userOrgId)
      } else if (orgs.length === 1) {
        setSelectedOrgId(orgs[0].id)
      }
    }
  }, [orgs, selectedOrgId, isOrgFixed, userOrgId])

  useEffect(() => {
    if (isLobFixed && userLobId && selectedOrgId) {
      setSelectedLobId(userLobId)
      if (!retentionLobId) setRetentionLobId(userLobId)
    } else if (selectedOrgId && !selectedLobId && lobs.length > 0) {
      if (userLobId && lobs.some(l => l.id === userLobId)) {
        setSelectedLobId(userLobId)
      } else if (lobs.length === 1) {
        setSelectedLobId(lobs[0].id)
      }
    }
  }, [lobs, selectedOrgId, selectedLobId, isLobFixed, userLobId])

  // Reset LOB when org changes manually
  useEffect(() => {
    if (selectedOrgId && authStore.user?.orgId !== selectedOrgId && !isLobFixed) {
      setSelectedLobId('')
    }
  }, [selectedOrgId, isLobFixed])

  // ── Mutations ─────────────────────────────────────────────────────────────
  const saveRetentionMutation = useMutation({
    mutationFn: (payload: { orgId: string | null; lobId: string | null }) =>
      settingsApi.updateRetentionKnowlaritySetting(payload),
    onSuccess: () => {
      toast.success('Retention calling settings saved successfully')
      queryClient.invalidateQueries({ queryKey: ['knowlarity-retention-setting'] })
    },
    onError: () => toast.error('Failed to save retention settings'),
  })
  const addMutation = useMutation({
    mutationFn: (payload: AddKnowlarityNumberPayload) => settingsApi.addKnowlarityNumber(payload),
    onSuccess: () => {
      toast.success('Number added successfully')
      setForm(emptyForm)
      queryClient.invalidateQueries({ queryKey: ['knowlarity-numbers', selectedOrgId, selectedLobId] })
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to add number'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => settingsApi.deleteKnowlarityNumber(id),
    onSuccess: () => {
      toast.success('Number deleted')
      setDeleteTarget(null)
      queryClient.invalidateQueries({ queryKey: ['knowlarity-numbers', selectedOrgId, selectedLobId] })
    },
    onError: () => toast.error('Failed to delete number'),
  })

  const toggleMutation = useMutation({
    mutationFn: (id: string) => settingsApi.toggleKnowlarityNumber(id),
    onSuccess: (res) => {
      toast.success(`Number ${res.isActive ? 'enabled' : 'disabled'}`)
      queryClient.invalidateQueries({ queryKey: ['knowlarity-numbers', selectedOrgId, selectedLobId] })
    },
    onError: () => toast.error('Failed to toggle number'),
  })

  const updateRotationMutation = useMutation({
    mutationFn: (freq: number) => settingsApi.updateKnowlarityRotation(selectedOrgId, selectedLobId, freq),
    onSuccess: () => {
      toast.success('Rotation frequency updated')
      queryClient.invalidateQueries({ queryKey: ['knowlarity-rotation', selectedOrgId, selectedLobId] })
    },
    onError: () => toast.error('Failed to update rotation frequency')
  })

  const overrideRotationMutation = useMutation({
    mutationFn: (id: string) => settingsApi.overrideKnowlarityRotation(selectedOrgId, selectedLobId, id),
    onSuccess: () => {
      toast.success('Active number overridden')
      queryClient.invalidateQueries({ queryKey: ['knowlarity-rotation', selectedOrgId, selectedLobId] })
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to override active number')
  })

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleAdd = () => {
    if (!selectedOrgId || !selectedLobId) {
      toast.error('Please select an organisation and a LOB first.')
      return
    }
    if (!form.kNumber.trim()) { toast.error('K-Number is required.'); return }
    if (!form.callerId.trim()) { toast.error('Caller ID is required.'); return }

    const phoneRegex = /^\+\d{10,15}$/;
    if (!phoneRegex.test(form.kNumber.trim())) {
      toast.error('K-Number must be in E.164 format (e.g. +918095958107)')
      return
    }
    if (!phoneRegex.test(form.callerId.trim())) {
      toast.error('Caller ID must be in E.164 format (e.g. +918095958107)')
      return
    }
    addMutation.mutate({
      orgId:    selectedOrgId,
      lobId:    selectedLobId,
      kNumber:  form.kNumber.trim(),
      callerId: form.callerId.trim(),
      label:    form.label.trim() || undefined,
    })
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const cardSx = {
    borderRadius: '14px',
    border: `1px solid ${themeColors.border}`,
    boxShadow: themeColors.shadow.sm,
    maxWidth: 700,
  }

  const labelSx = { fontSize: '12px', color: themeColors.text.secondary, mb: 0.5 }

  return (
    <Box>
      <PageHeader
        title="Knowlarity Telephony Settings"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb={true}
      />

      <Box sx={{ px: 3, pb: 4 }}>
        <Card sx={cardSx}>
          <CardContent sx={{ p: 4 }}>

            {/* ── Header ───────────────────────────────────────────────────── */}
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: themeColors.text.primary, mb: 0.5 }}>
              Virtual Numbers by LOB
            </Typography>
            <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, mb: 3 }}>
              Configure one or more (K-Number, Caller ID) pairs per Line of Business.
              When an agent dials a customer, one number is picked randomly from the active pool.
            </Typography>

            {/* ── Scope selectors ───────────────────────────────────────────── */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Organisation</InputLabel>
                <Select
                  value={selectedOrgId}
                  label="Organisation"
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                  disabled={isLoadingOrgs || isOrgFixed}
                >
                  {orgs.map((org) => (
                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Line of Business</InputLabel>
                <Select
                  value={selectedLobId}
                  label="Line of Business"
                  onChange={(e) => setSelectedLobId(e.target.value)}
                  sx={{ borderRadius: '8px' }}
                  disabled={!selectedOrgId || isLoadingLobs || isLobFixed}
                >
                  {lobs.map((lob) => (
                    <MenuItem key={lob.id} value={lob.id}>{lob.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* ── Number list ───────────────────────────────────────────────── */}
            {selectedOrgId && selectedLobId && (
              <>
                <Divider sx={{ mb: 2 }} />

                {isLoadingNumbers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : numbers.length === 0 ? (
                  <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, textAlign: 'center', py: 2 }}>
                    No numbers configured yet for this LOB.
                  </Typography>
                ) : (
                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    {numbers.map((num) => (
                      <Box
                        key={num.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          p: 1.5,
                          borderRadius: '10px',
                          border: `1px solid ${themeColors.border}`,
                          bgcolor: num.isActive ? 'transparent' : 'rgba(0,0,0,0.03)',
                          opacity: num.isActive ? 1 : 0.6,
                        }}
                      >
                        {/* Status chip */}
                        <Chip
                          label={num.isActive ? 'Active' : 'Off'}
                          size="small"
                          sx={{
                            minWidth: 52,
                            fontWeight: 600,
                            fontSize: '11px',
                            bgcolor: num.isActive ? '#dcfce7' : '#f3f4f6',
                            color: num.isActive ? '#16a34a' : '#6b7280',
                          }}
                        />

                        {/* Number details */}
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary }}>
                            {num.kNumber}
                            <Typography component="span" sx={{ fontSize: '12px', color: themeColors.text.secondary, ml: 1 }}>
                              / {num.callerId}
                            </Typography>
                          </Typography>
                          {num.label && (
                            <Typography sx={{ fontSize: '11px', color: themeColors.text.secondary }}>
                              {num.label}
                            </Typography>
                          )}
                        </Box>

                        {/* Actions */}
                        {canUpdate && (
                          <>
                            <Tooltip title={num.isActive ? 'Disable' : 'Enable'}>
                              <IconButton
                                size="small"
                                onClick={() => toggleMutation.mutate(num.id)}
                                disabled={toggleMutation.isPending}
                                sx={{ color: num.isActive ? '#16a34a' : '#9ca3af' }}
                              >
                                <PowerSettingsNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => setDeleteTarget(num)}
                                sx={{ color: '#ef4444' }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}

                {/* ── Add form ─────────────────────────────────────────────── */}
                {canUpdate && (
                  <>
                    <Divider sx={{ mb: 2 }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: themeColors.text.primary, mb: 1.5 }}>
                      Add New Number
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={labelSx}>K-Number *</Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="+918095958107"
                          value={form.kNumber}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[+\d]*$/.test(val)) setForm(f => ({ ...f, kNumber: val }));
                          }}
                          disabled={addMutation.isPending}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={labelSx}>Caller ID *</Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="+918069792576"
                          value={form.callerId}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^[+\d]*$/.test(val)) setForm(f => ({ ...f, callerId: val }));
                          }}
                          disabled={addMutation.isPending}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={labelSx}>Label (optional)</Typography>
                        <TextField
                          size="small"
                          fullWidth
                          placeholder="e.g. Delhi DID"
                          value={form.label}
                          onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))}
                          disabled={addMutation.isPending}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                      </Box>
                    </Stack>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleAdd}
                        disabled={addMutation.isPending}
                        sx={{
                          bgcolor: themeColors.primary,
                          borderRadius: '8px',
                          px: 3,
                          py: 1,
                          fontWeight: 600,
                          textTransform: 'none',
                          '&:hover': { bgcolor: themeColors.primaryDark },
                        }}
                      >
                        {addMutation.isPending ? 'Adding...' : 'Add Number'}
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}

            {/* Prompt when no scope selected */}
            {(!selectedOrgId || !selectedLobId) && (
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, textAlign: 'center', py: 2 }}>
                Select an organisation and a line of business to manage its numbers.
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* ── Rotation Config Card ─────────────────────────────────────────── */}
        {selectedOrgId && selectedLobId && (
          <Card sx={{ ...cardSx, mt: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: themeColors.text.primary, mb: 0.5 }}>
                Number Rotation Schedule
              </Typography>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, mb: 3 }}>
                Automatically rotate the active virtual number based on a set frequency (in hours).
              </Typography>

              {isLoadingRotation ? (
                 <CircularProgress size={24} />
              ) : (
                <Stack spacing={3}>
                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>Current Active Number</Typography>
                    {rotationConfig?.activeNumber ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: '#f8fafc', borderRadius: '8px', border: `1px solid ${themeColors.border}` }}>
                        <Chip label="Active" size="small" sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600, fontSize: '11px' }} />
                        <Box>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{rotationConfig.activeNumber.kNumber} / {rotationConfig.activeNumber.callerId}</Typography>
                          {rotationConfig.activeNumber.label && <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>{rotationConfig.activeNumber.label}</Typography>}
                        </Box>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>No active numbers available for rotation.</Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>Rotation Frequency (Hours)</Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <TextField
                        size="small"
                        type="number"
                        value={frequencyHours}
                        onChange={(e) => setFrequencyHours(e.target.value ? parseInt(e.target.value) : '')}
                        disabled={!canUpdate || updateRotationMutation.isPending}
                        sx={{ width: 120, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        InputProps={{ inputProps: { min: 1 } }}
                      />
                      {canUpdate && (
                        <Button
                          variant="outlined"
                          disabled={!frequencyHours || frequencyHours === rotationConfig?.frequencyHours || updateRotationMutation.isPending}
                          onClick={() => updateRotationMutation.mutate(frequencyHours as number)}
                          sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}
                        >
                          {updateRotationMutation.isPending ? 'Saving...' : 'Save Frequency'}
                        </Button>
                      )}
                    </Stack>
                    {rotationConfig?.nextRotationAt && (
                      <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mt: 1 }}>
                        Next rotation will happen at: {new Date(rotationConfig.nextRotationAt + (rotationConfig.nextRotationAt.endsWith('Z') ? '' : 'Z')).toLocaleString()} {timeLeft && <strong style={{color: themeColors.primary}}>({timeLeft})</strong>}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>Manual Override</Typography>
                    <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, mb: 2 }}>
                      Force the rotation to start from a specific number immediately. Automatic rotation will continue from this number after the set frequency.
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {numbers.filter(n => n.isActive).map(num => (
                        <Button
                          key={num.id}
                          variant={rotationConfig?.activeNumber?.id === num.id ? 'contained' : 'outlined'}
                          size="small"
                          disabled={!canUpdate || overrideRotationMutation.isPending}
                          onClick={() => overrideRotationMutation.mutate(num.id)}
                          sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '12px' }}
                        >
                          {num.kNumber} / {num.callerId}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        )}

        {/* ── Retention Customers Call Mapping Card ─────────────────────── */}
        {canSetRetention && (
          <Card sx={{ ...cardSx, mt: 3 }}>
            <CardContent sx={{ p: 4 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '16px', color: themeColors.text.primary, mb: 0.5 }}>
                Retention Customers Telephony Mapping
              </Typography>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, mb: 3 }}>
                Select the Organization and Line of Business (LOB) whose Knowlarity virtual numbers will be used for outgoing calls to Retention Customers.
              </Typography>

              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Organisation</InputLabel>
                  <Select
                    value={retentionOrgId}
                    label="Organisation"
                    onChange={(e) => {
                      setRetentionOrgId(e.target.value)
                      setRetentionLobId('')
                    }}
                    sx={{ borderRadius: '8px' }}
                    disabled={isLoadingOrgs || isOrgFixed}
                  >
                    {orgs.map((org) => (
                      <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Line of Business</InputLabel>
                  <Select
                    value={retentionLobId}
                    label="Line of Business"
                    onChange={(e) => setRetentionLobId(e.target.value)}
                    sx={{ borderRadius: '8px' }}
                    disabled={!retentionOrgId || isLoadingRetentionLobs || isLobFixed}
                  >
                    {retentionLobs.map((lob) => (
                      <MenuItem key={lob.id} value={lob.id}>{lob.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {canUpdate && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={() => saveRetentionMutation.mutate({ orgId: retentionOrgId || null, lobId: retentionLobId || null })}
                    disabled={saveRetentionMutation.isPending || !retentionOrgId || !retentionLobId || !isRetentionDirty}
                    sx={{
                      bgcolor: themeColors.primary,
                      borderRadius: '8px',
                      px: 3,
                      py: 1,
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { bgcolor: themeColors.primaryDark },
                    }}
                  >
                    {saveRetentionMutation.isPending ? 'Saving...' : 'Save Retention Mapping'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* ── Delete confirm dialog ─────────────────────────────────────────── */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Number"
        description={
          deleteTarget
            ? `Are you sure you want to delete ${deleteTarget.kNumber} / ${deleteTarget.callerId}${deleteTarget.label ? ` (${deleteTarget.label})` : ''}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmColor="error"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </Box>
  )
})

export default KnowlaritySettingsPage
